import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getEmailValidationError, sanitizeEmailInput } from '../../utils/emailValidation';
import AuthLayout from './AuthLayout';

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:3000/api').replace(/\/$/, '');

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [showLoading, setShowLoading] = useState(false);
  const [localError, setLocalError] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const navigate = useNavigate();

  const emailFormatError = useMemo(
    () => getEmailValidationError(email, { required: false }),
    [email]
  );
  const submitDisabled = showLoading || Boolean(emailFormatError);

  const handleChange = (e) => {
    setEmail(sanitizeEmailInput(e.target.value));
    if (localError) {
      setLocalError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    setLocalError('');

    const emailErr = getEmailValidationError(email, { required: true });
    if (emailErr) {
      setLocalError(emailErr);
      return;
    }

    setShowLoading(true);

    try {
      const response = await fetch(`${API_BASE}/users/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ email: email.trim() }),
      });

      const data = await response.json();

      if (!response.ok) {
        setLocalError(data.message || `Request failed (HTTP ${response.status})`);
        return;
      }

      if (!data.success) {
        setLocalError(data.message || 'Request failed');
        return;
      }

      setShowSuccessModal(true);
    } catch (error) {
      console.error('Forgot password error:', error);
      setLocalError('An error occurred. Please try again.');
    } finally {
      setShowLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowSuccessModal(false);
    navigate('/login');
  };

  return (
    <>
      {showSuccessModal && (
        <div className="lp-modal-overlay" onClick={handleCloseModal}>
          <div className="lp-modal" onClick={(e) => e.stopPropagation()}>
            <div className="lp-modal-icon">✓</div>
            <h4 className="lp-modal-title">Check Your Email</h4>
            <p className="lp-modal-text">
              We have sent a password reset link to your email address.
            </p>
            <p className="lp-modal-hint">
              Please check your inbox and follow the instructions.
            </p>
            <button
              type="button"
              className="lp-btn-signin"
              onClick={handleCloseModal}
            >
              Return to Sign In
            </button>
          </div>
        </div>
      )}

      <AuthLayout
        title="Forgot Password?"
        subtitle="Enter your email address and we'll send you a link to reset your password."
      >
        <form onSubmit={handleSubmit} className="lp-auth-form" noValidate>
          <div className="lp-form-group">
            <label htmlFor="fp-email" className="lp-label">
              Email Address <span className="lp-required">*</span>
            </label>
            <div className="lp-input-container">
              <input
                id="fp-email"
                type="email"
                name="email"
                value={email}
                onChange={handleChange}
                placeholder="Enter your registered email address"
                className="lp-input"
                required
                autoFocus
              />
            </div>
          </div>

          {localError && <p className="lp-form-error">{localError}</p>}

          <button
            type="submit"
            className="lp-btn-signin"
            disabled={submitDisabled}
          >
            {showLoading && <span className="lp-btn-spinner" />}
            <span>{showLoading ? 'Sending Reset Link...' : 'Send Reset Link'}</span>
          </button>

          <div className="lp-back-wrap">
            <a
              href="/login"
              className="lp-forgot-pass-link"
              onClick={(e) => {
                e.preventDefault();
                navigate('/login');
              }}
            >
              ← Back to Sign In
            </a>
          </div>
        </form>
      </AuthLayout>
    </>
  );
};

export default ForgotPasswordPage;
